import {
  BarChartIcon,
  LineChartIcon,
  PieChartIcon,
  ScatterChartIcon,
  AreaChartIcon,
  LayoutGridIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export type GraphType =
  | "bar"
  | "line"
  | "area"
  | "pie"
  | "donut"
  | "scatter"
  | "composed";

export const GraphTypeIcons = {
  bar: BarChartIcon,
  line: LineChartIcon,
  area: AreaChartIcon,
  pie: PieChartIcon,
  donut: PieChartIcon,
  scatter: ScatterChartIcon,
  composed: LayoutGridIcon,
};

export const GraphTypeExplanations = {
  bar: "Idéal pour comparer des valeurs entre catégories. Parfait pour les données discrètes.",
  line: "Parfait pour montrer les tendances et l'évolution des données dans le temps.",
  area: "Similaire aux graphiques en ligne mais met l'accent sur le volume sous la courbe.",
  pie: "Excellent pour illustrer les proportions d'un tout et les distributions en pourcentage.",
  donut:
    "Excellent pour illustrer les proportions d'un tout et les distributions en pourcentage.",
  scatter:
    "Utile pour identifier les corrélations et les tendances entre deux variables.",
  composed:
    "Combine plusieurs types de graphiques pour comparer différentes mesures.",
};

interface GraphTypeSelectorProps {
  value: GraphType;
  onChange: (value: GraphType) => void;
  showDescription?: boolean;
  className?: string;
  disabled?: boolean;
}

export const GraphTypeSelector = ({
  value,
  onChange,
  showDescription = true,
  className,
  disabled = false,
}: GraphTypeSelectorProps) => {
  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <Select
        value={value}
        onValueChange={(newValue: GraphType) => onChange(newValue)}
        disabled={disabled}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder="Sélectionnez un type">
            {value && (
              <div className="flex items-center gap-2">
                {(() => {
                  const Icon = GraphTypeIcons[value];
                  return <Icon className="h-4 w-4" />;
                })()}
                <span className={cn("capitalize")}>{value}</span>
              </div>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <TooltipProvider>
              {(
                [
                  "bar",
                  "line",
                  "area",
                  "pie",
                  "donut",
                  "scatter",
                  "composed",
                ] as const
              ).map((type) => {
                const Icon = GraphTypeIcons[type];
                return (
                  <Tooltip key={type}>
                    <TooltipTrigger asChild>
                      <div>
                        <SelectItem value={type}>
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4" />
                            <span className="capitalize">{type}</span>
                          </div>
                        </SelectItem>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-xs text-xs">
                      {GraphTypeExplanations[type]}
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </TooltipProvider>
          </SelectGroup>
        </SelectContent>
      </Select>
      {showDescription && value && (
        <p className="text-xs text-muted-foreground">
          {GraphTypeExplanations[value]}
        </p>
      )}
    </div>
  );
};
